import { useEffect, useRef } from "react"

import { Form, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import BusinessInfoForm from "../../../Components/Forms/BusinessInfoForm"
import { usePhoneCountries } from "../../../hooks/usePhoneCountries"
import Icon from "../../../Icon"
import { bindPendingProviderBanner, setProviderBanner } from "../../../Utils/anotherProviderBanner"
import {
  CHOICE_FLOWS,
  choiceIncludes,
  FEATURES,
  hasOnboardingProgress,
  resolveProductChoice
} from "../../../Utils/onboarding/productChoice"
import { createAccountStepPath, FEATURES_STEP_PATH, onboardingStepPath } from "../../../Utils/utils"
import { OB_STEPS } from "../../GettingStartedPage/utils/onboardingSteps"
import { authErrorReset } from "../../LoginPage/redux/auth.actions"
import { userActions } from "../../User/redux/user.slice"
import {
  changeOnboardingStep,
  createBusinessAccount,
  getUserInfo,
  trackEvent,
  updateBusinessAccount
} from "../../User/redux/user.thunks"
import { updateCompletedStep } from "../../User/services"

import "./styles.less"

const OnboardingBusinessInfoPage = (props) => {
  const { step } = props
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { countries = [], loading } = usePhoneCountries()

  const { error, fetching, userInfo, isAdmin } = useSelector((state) => state.auth)
  const businessAccountsFetching = useSelector((state) => state.user.businessAccountsFetching)
  const businessAccountFetching = useSelector((state) => state.user.businessAccountFetching)
  const businessAccount = useSelector((state) => state.user.businessAccount)
  const nextCreateAccountStep = useSelector((state) => state.user.nextCreateAccountStep)
  const createAccountBusinessId = useSelector((state) => state.user.createAccountBusinessId)
  const saveResult = useSelector((state) => state.user.saveResult)
  const isOnboarding = pathname.includes("/onboarding")
  const isCreateBusinessInfoStep = pathname.includes("/create-account/business-info")

  const editableBusinessId =
    nextCreateAccountStep === 1 ? createAccountBusinessId : businessAccount?.id

  // The alert-provider question only makes sense with Prevent in the picture; a Recover-only
  // merchant never sees it and is never blocked by it (OB-5-06, OB-5-08).
  const choiceFlow = isOnboarding ? CHOICE_FLOWS.ONBOARDING : CHOICE_FLOWS.BUSINESS
  const productChoice = resolveProductChoice({
    flow: choiceFlow,
    hasProgress: hasOnboardingProgress({ flow: choiceFlow, userInfo, nextCreateAccountStep })
  })
  const askAlertProvider = choiceIncludes(productChoice, FEATURES.PREVENT)

  // Back returns to the product choice; the features step decides on its own whether a changed
  // selection needs the reset confirmation (OB-5-18).
  const featuresPath = isOnboarding ? FEATURES_STEP_PATH.onboarding : FEATURES_STEP_PATH.business
  const goBackToFeatures = () => {
    navigate(featuresPath)
  }

  const getPhoneData = () => {
    if (userInfo?.phone) {
      const phone = userInfo?.phone
      const phoneCode = userInfo?.phone_code
      const codeLength = phoneCode?.code.length + 1
      return {
        countryCode: phoneCode?.country_code,
        code: phoneCode.code,
        number: phone.slice(codeLength),
        name: phoneCode.name,
        id: phoneCode.id
      }
    }
    return {
      countryCode: countries[19]?.country_code || "",
      code: countries[19]?.code || "",
      number: "",
      name: countries[19]?.name || "",
      id: countries[19]?.id
    }
  }

  const getInitialBusinessValues = () => {
    const shouldShowEmptyValues =
      !businessAccount || (isCreateBusinessInfoStep && nextCreateAccountStep === 1)

    if (shouldShowEmptyValues) {
      return {
        name: "",
        url: "",
        is_use_another_solutions: false,
        another_solution_name: "",
        previously_connected_enrollments: [],
        is_user_notified_previous_solution: false,
        phone: getPhoneData()
      }
    }

    return {
      name: businessAccount?.name || "",
      url: businessAccount?.url || "",
      is_use_another_solutions: businessAccount?.is_use_another_solutions || false,
      another_solution_name: businessAccount?.another_solution_name || "",
      previously_connected_enrollments: businessAccount?.previously_connected_enrollments || [],
      is_user_notified_previous_solution:
        businessAccount?.is_user_notified_previous_solution || false,
      phone: getPhoneData()
    }
  }

  function handleSubmit(data) {
    const fullData = {
      ...data,
      phone:
        data.phone?.number?.trim() && data.phone?.code
          ? `+${data.phone.code}${data.phone.number.trim()}`
          : "",
      country_name: data.phone.name,
      phone_code_id: data.phone.id
    }

    if (isOnboarding) {
      setProviderBanner(businessAccount?.id, data.is_use_another_solutions)
    }

    if (editableBusinessId) {
      dispatch(
        updateBusinessAccount({
          id: editableBusinessId,
          ...fullData
        })
      )
    } else {
      dispatch(createBusinessAccount(fullData))
    }
  }

  useEffect(() => {
    dispatch(getUserInfo())
    document.title = "Business Info - Chargeback"
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [pathname])

  // Pre-fill from saved values without discarding what the user already typed: the sources
  // (businessAccount refetch goes null -> object, countries arrive late) settle after the form is
  // shown, and every resetFields() remounts the fields — wiping input and closing the country list.
  const lastFilledRef = useRef({ signature: null, pathname: null })

  useEffect(() => {
    if (!userInfo || isAdmin || businessAccountFetching || loading) return

    const values = getInitialBusinessValues()
    const signature = JSON.stringify(values)
    const lastFilled = lastFilledRef.current

    // A new route is a new form (onboarding vs create-account reuse this instance) — always refill.
    // On the same route refill only when the saved data really changed and nothing was typed yet.
    if (pathname === lastFilled.pathname) {
      if (signature === lastFilled.signature || form.isFieldsTouched()) return
    }

    form.resetFields()
    form.setFieldsValue(values)
    lastFilledRef.current = { signature, pathname }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [
    businessAccount,
    businessAccountFetching,
    loading,
    countries.length,
    nextCreateAccountStep,
    pathname
  ])

  useEffect(() => {
    if (error) dispatch(authErrorReset())
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [error])

  useEffect(() => {
    if (saveResult && !saveResult?.confirm) {
      if (userInfo?.new_onboarding_finished) {
        if (nextCreateAccountStep && businessAccount?.id) {
          bindPendingProviderBanner(businessAccount.id)
          updateCompletedStep({
            step,
            business_account_id: businessAccount?.id
          })
          navigate(createAccountStepPath[step + 1])
        }
      } else {
        bindPendingProviderBanner(businessAccount?.id || userInfo?.current_business_account_id)

        // The backend moves the pointer past this step only when it creates the business. A
        // merchant who came back here (the features step reset the flow, or their business
        // already existed) saves an update instead, and the pointer would stay on Company
        // profile, reading every later step as not done. Move it here whenever it is behind;
        // the step-change saga lands on the next step itself.
        const nextObStep = OB_STEPS.INTEGRATION

        if ((userInfo?.next_step ?? OB_STEPS.EMAIL) < nextObStep) {
          dispatch(changeOnboardingStep({ next_step: nextObStep }))
        } else {
          navigate(onboardingStepPath[nextObStep])
        }
      }

      dispatch(userActions.userSaveResultReset())
    }
  }, [saveResult])

  useEffect(() => {
    const loaded = isOnboarding ? Boolean(userInfo?.id) : nextCreateAccountStep != null

    if (loaded && !isAdmin && productChoice === null) navigate(featuresPath, { replace: true })
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [productChoice === null, userInfo?.id, nextCreateAccountStep, isOnboarding, isAdmin])

  useEffect(() => {
    dispatch(trackEvent({ event_type: "first_step" }))
  }, [])

  return (
    <div className="ob-business">
      <Spin size="large" spinning={fetching || businessAccountsFetching}>
        <div className="ob-business__row">
          <div className="ob-business__content">
            <div className="ob-business__main ob-business__main--form">
              <div className="ob-business__card">
                <ButtonComponent
                  type="text"
                  size="small"
                  className="ob-business__back flex center"
                  icon={<Icon type="arrow_left" />}
                  onClick={goBackToFeatures}
                >
                  {t("Back")}
                </ButtonComponent>

                <h2 className="ob-business__title">{t("Business information")}</h2>

                <h4 className="ob-business__subtitle">
                  {t("Let’s set up your Chargeback account.")}
                </h4>

                <BusinessInfoForm
                  form={form}
                  onSubmit={handleSubmit}
                  onboarding={!userInfo?.new_onboarding_finished}
                  askAlertProvider={askAlertProvider}
                  disabled={loading && !countries.length}
                />
              </div>
            </div>
          </div>
        </div>
      </Spin>
    </div>
  )
}

export default OnboardingBusinessInfoPage
